import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { observer } from 'mobx-react-lite';
import React, { useMemo } from 'react';
import styled, { keyframes } from 'styled-components';

import TimelinePhotoIcon from '../../svgs/TimelinePhotoIcon';

import type { TranscriptToken } from '../../editor/types';
import type { KaraokeLine } from '../../editor/types/karaoke';
import type { ElementState } from '../../renderer/ElementState';

type ElementProps = {
  value: string | React.JSX.Element | null;
  tokenId: string;
  lineId: string;
  index: number;
  isHighlighted: boolean;
  isHidden: boolean;
  isPhotoHighlight: boolean;
  karaokeBreak?: boolean;
  isAfterBreak?: boolean;
};

const Element = React.memo((props: ElementProps) => {
  const {
    value,
    tokenId,
    lineId,
    index,
    isHighlighted,
    isHidden,
    isPhotoHighlight,
    karaokeBreak,
    isAfterBreak,
  } = props;
  const valueNode = value === '\n' ? '' : value;

  return (
    <Word
      key={`element-${tokenId}`}
      data-index={index}
      data-token-id={tokenId}
      data-line-id={lineId}
      isHighlighted={isHighlighted}
      isHidden={isHidden}
      isPhotoHighlight={isPhotoHighlight}
      karaokeBreak={karaokeBreak}
      preWrapWhitespace={
        typeof value === 'string' && value.trim() === '' && !isAfterBreak
      }
    >
      {valueNode}
    </Word>
  );
});

type Props = {
  cursor: { from: number; to: number };
  karaokeLines: KaraokeLine[];
};

export const KaraokeLinesView: React.FC<Props> = observer(
  ({ cursor, karaokeLines }: Props) => {
    const videoCreator = useVideoCreatorStore();

    // Flatten karaoke lines into a single array with break markers
    const flattenedTokens = useMemo(() => {
      const result: Array<{
        token: TranscriptToken;
        isHidden: boolean;
        isLastInLine: boolean;
        lineId: string;
        globalIndex: number;
      }> = [];

      let globalIndex = 0;
      karaokeLines.forEach(line => {
        line.transcriptTokens.forEach((token, tokenIndex) => {
          const isLastInLine = tokenIndex === line.transcriptTokens.length - 1;
          result.push({
            token,
            isHidden:
              line.allHiddenTranscriptTokenIds?.includes(token.id) ?? false,
            isLastInLine,
            lineId: line.id,
            globalIndex: globalIndex++,
          });
        });
      });

      return result;
    }, [karaokeLines]);

    let isBreak = false;

    return (
      <>
        {flattenedTokens.map((item, index) => {
          const { token, isHidden, isLastInLine, globalIndex } = item;

          let isAfterBreak = false;
          if (isBreak) {
            isAfterBreak = true;
          }
          isBreak = isLastInLine;

          let hasPhotoHighlight = false;
          let element: ElementState | undefined = undefined;

          // Check for photo highlights
          if (token.last_photo_highlight && videoCreator.state?.elements) {
            element = videoCreator.state!.elements.find(
              e => e.source.id === token.photo_highlight_id,
            );
            hasPhotoHighlight = !!element?.source?.source;
          }

          return (
            <React.Fragment key={token.id}>
              {!videoCreator.isPlaying && globalIndex === cursor.from ? (
                <Cursor data-index={cursor.from} />
              ) : null}
              <Element
                karaokeBreak={isLastInLine}
                isAfterBreak={isAfterBreak}
                value={token.value}
                tokenId={token.id}
                lineId={item.lineId}
                index={globalIndex}
                isHighlighted={Boolean(
                  videoCreator.isPlaying &&
                    globalIndex >= cursor.from &&
                    globalIndex < cursor.to,
                )}
                isHidden={isHidden}
                isPhotoHighlight={!!token.photo_highlight_id}
              />
              {!videoCreator.isPlaying &&
              index === flattenedTokens.length - 1 &&
              globalIndex + 1 === cursor.from ? (
                <Cursor data-index={cursor.from} />
              ) : null}
              {token.last_photo_highlight ? (
                <HighlightImageWrapper>
                  <HighlightImage
                    isAssigned={hasPhotoHighlight}
                    key={`highlight-${globalIndex}`}
                    onClick={() => {
                      if (!element) return;
                      videoCreator.setActiveElements(element?.source.id);
                    }}
                  >
                    <TimelinePhotoIcon
                      width="10"
                      height="10"
                      strokeColor={hasPhotoHighlight ? '#fff' : '#bdbdbd'}
                    ></TimelinePhotoIcon>
                  </HighlightImage>
                </HighlightImageWrapper>
              ) : null}
            </React.Fragment>
          );
        })}
      </>
    );
  },
);

const colorByState = (
  isHighlighted: boolean,
  isHidden: boolean,
  isPhotoHighlight: boolean,
) => {
  if (isHighlighted) return 'black';
  if (isPhotoHighlight) return '#45d483';
  if (isHidden) return '#F69B12'; // Hidden tokens styled similarly to cut tokens
  return '#bdbdbd'; // Default color
};

export const Word = styled.span.attrs(
  (props: {
    isHighlighted: boolean;
    karaokeBreak?: boolean;
    isHidden: boolean;
    isPhotoHighlight: boolean;
    preWrapWhitespace?: boolean;
  }) => props,
)`
  color: ${props =>
    colorByState(props.isHighlighted, props.isHidden, props.isPhotoHighlight)};
  background-color: ${props => (props.isHighlighted ? '#dfb615' : 'none')};
  cursor: pointer;
  text-decoration: ${props => (props.isPhotoHighlight ? 'underline' : 'none')};
  opacity: ${props => (props.isHidden ? 0.9 : 1)};

  ${props => props.preWrapWhitespace && `white-space: pre-wrap;`}

  &:hover {
    color: lightgreen;
  }

  ${props =>
    props.karaokeBreak &&
    `
    margin-right: 3px;

    &::before {
      content: '';
      position: absolute;
      display: inline-block;
      width: 87%;
      height: 1px;
      background-color: #333333;
      left: 5px;
      margin-top: 38px;
    }

    &::after {
      content: '\\a\\a';
      white-space: pre;
    }
    `}
`;

const cursorAnimation = keyframes`
  100% {
    opacity: 0;
  }
`;

export const Cursor = styled.span`
  animation: ${cursorAnimation} steps(2) 1.5s infinite;
  height: 26px;
  margin-bottom: -8px;
  width: 3px;
  background-color: #2a85ff;
  display: inline-block;
  border-radius: 1px;
`;

export const HighlightImageWrapper = styled.div`
  position: relative;
  display: inline;
`;

export const HighlightImage = styled.div.attrs(
  (props: { isAssigned: boolean }) => props,
)`
  background: ${props =>
      props.isAssigned
        ? 'linear-gradient(0deg, #45d483 0%, #45d483 100%), #45d483'
        : 'linear-gradient(0deg, #4f4f4f 0%, #4f4f4f 100%), lightgray'}
    50% / cover no-repeat;
  color: #4f4f4f;
  border-radius: 50%;
  padding: 6px;
  margin-left: 5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
`;
